import { useQuery } from '@tanstack/react-query';
import { CheckCircle2 } from 'lucide-react';
import type React from 'react';
import { partnerApi } from '@/lib/api';
import { useAllFlags } from '@/hooks/useAllFlags';
import type { SenderDetailsUpdate, SenderProfile } from '@reach/shared-types/schema-mappings';
import styles from './SenderDetailsSelector.module.css';

interface SenderDetailsSelectorProps {
  senderProfileId: string | null | undefined;
  usePointOfContactSender?: boolean;
  onChange: (update: SenderDetailsUpdate) => void;
}

export function SenderDetailsSelector({
  senderProfileId,
  usePointOfContactSender,
  onChange,
}: SenderDetailsSelectorProps) {
  const { ld, partnerFlags } = useAllFlags();

  const senderProfilesEnabled = ld['enable-sender-profiles'];

  const { data: schemaMappings } = useQuery({
    queryKey: ['partner-schema-mappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
    staleTime: 5 * 60 * 1000,
    enabled: !!senderProfilesEnabled,
  });

  if (!partnerFlags?.engage?.enablePointOfContactSender) {
    return null;
  }

  const profiles: SenderProfile[] = senderProfilesEnabled
    ? (schemaMappings?.contactsSchema?.primarySource?.senderProfiles ?? [])
    : [];

  const usesSenderProfiles = senderProfileId !== null && senderProfileId !== undefined;
  const isEnabled = usesSenderProfiles || usePointOfContactSender === true;

  const handleToggleOff = () => onChange({ senderProfileId: null, usePointOfContactSender: false });

  const handleToggleOn = () => {
    const hasProfiles = profiles.length >= 1;
    const newSenderProfileId = hasProfiles ? profiles[0].profileId : null;
    onChange({ senderProfileId: newSenderProfileId, usePointOfContactSender: true });
  };

  const handleSenderProfileChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onChange({ senderProfileId: e.target.value || null, usePointOfContactSender: true });
  };

  const senderProfileDescription = usesSenderProfiles
    ? `Send using the name and email of the customer's`
    : 'Send using the name and email of the contact person assigned to each customer.';

  return (
    <fieldset className={styles.senderDetailsSelector}>
      <legend>Sender Details</legend>
      <p>This selection applies to every email in this automation.</p>
      <div className={styles.senderOptions}>
        <button type="button" aria-pressed={!isEnabled} onClick={handleToggleOff}>
          {!isEnabled && <CheckCircle2 className={styles.checkIcon} />}
          <span>Business default</span>
          <span>Send using this business's configured sender name and reply-to address.</span>
        </button>
        <div className={styles.pocOption}>
          <button type="button" aria-pressed={isEnabled} onClick={handleToggleOn}>
            {isEnabled && <CheckCircle2 className={styles.checkIcon} />}
            <span>Customer's point of contact</span>
            <span>{senderProfileDescription}</span>
          </button>
          {isEnabled && profiles.length > 1 && (
            <select value={senderProfileId ?? ''} onChange={handleSenderProfileChange}>
              {profiles.map(profile => (
                <option key={profile.profileId} value={profile.profileId}>
                  {profile.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>
    </fieldset>
  );
}
